import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
// ✅ FIX: Use a default import for the service object
import courseService from '../../../services/courseService';
import type { ICourse } from '../../../types/course';
import Loader from '../../../components/common/Loader';
import { showToast } from '../../../utils/showToast';
import { ArrowRight } from 'lucide-react';

const DUMMY_COURSE_IMAGE = '/default-course-image.jpg'; // A local fallback image

const CoursesSection: React.FC = () => {
  const [courses, setCourses] = useState<ICourse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCoursesFromBackend = async () => {
      setIsLoading(true);
      try {
        // ✅ FIX: Call the correct function from the service object
        const data = await courseService.getAllPublishedCourses();
        // ✅ FIX: No need to filter on the frontend, the service already does this
        setCourses(data); 
      } catch (err: any) {
        setError(err.message);
        showToast(err.message, { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCoursesFromBackend();
  }, []);

  if (isLoading) {
    return (
      <section className="py-20 bg-gray-50 flex justify-center items-center min-h-[300px]">
        <Loader />
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-gray-50 flex justify-center items-center min-h-[300px]">
        <p className="text-red-600 font-medium">Error: {error}</p>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Core Courses</h2>
          <p className="text-xl text-gray-600">
            Explore our comprehensive range of programs with practical mentorship.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {courses.length === 0 ? (
            <p className="text-center text-gray-500 col-span-full">No courses available at the moment.</p>
          ) : (
            courses.map((course) => (
              // ✅ FIX: Link now uses the slug-based URL pattern
              <Link key={course._id} to={`/courses/slug/${course.slug}`} className="block group">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden h-full flex flex-col group-hover:shadow-xl transition-shadow duration-300">
                  <div className="overflow-hidden">
                    <img
                      src={course.image || DUMMY_COURSE_IMAGE}
                      alt={course.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2 truncate">{course.title}</h3>
                      <p className="text-sm text-gray-700 h-20">{course.description.substring(0, 120)}...</p>
                    </div>
                    <div className="mt-auto pt-4 flex items-center justify-between text-sm text-gray-600">
                      <p className="font-medium">By {course.instructorName}</p>
                      <p className="font-bold text-lg text-blue-600">{course.price ? `₹${course.price.toLocaleString()}` : 'Paid'}</p>
                    </div>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>

        <div className="text-center mt-12">
          <Link to="/courses" className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-flex items-center space-x-2">
            <span>View All Courses</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CoursesSection;